import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { API_GET, useConfig } from '../../api/api';
import { DocIcon } from './DocIcon';
import { formatBytes, isHostedMedia, mediaId } from './media';
import { useWatchTracker } from './useWatchTracker';

// Resolves a media object to a playable src. Preferred: the backend's signed URL,
// which the browser can stream natively (range requests, no full download upfront).
// Fallback for hosted media without one: authenticated blob fetch through the
// backend proxy, since media tags cannot send the Authorization header themselves.
const useMediaObjectUrl = (media) => {
  const config = useConfig();
  const [src, setSrc] = useState(
    media?.signedUrl ?? (media && !isHostedMedia(media) ? media.url : null)
  );

  useEffect(() => {
    if (media?.signedUrl) {
      setSrc(media.signedUrl);
      return;
    }
    if (media && !isHostedMedia(media)) {
      setSrc(media.url);
      return;
    }
    if (!config) return;
    let objectUrl;
    let cancelled = false;
    fetch(`${config.API_URL}documentation-media/${mediaId(media)}`, API_GET)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setSrc(objectUrl);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [media?.url, media?.signedUrl, media?.isHosted, config]); // eslint-disable-line react-hooks/exhaustive-deps

  return src;
};

const placeholderSx = {
  aspectRatio: '16/9',
  borderRadius: '14px',
  background: 'linear-gradient(140deg,#2e3644,#1d222c)',
  boxShadow: 'var(--shadow)',
};

export const DocumentationVideo = ({ media }) => {
  const src = useMediaObjectUrl(media);
  const trackerRef = useWatchTracker(media);

  if (!src)
    return (
      <Box className="flex items-center justify-center" sx={placeholderSx}>
        <DocIcon name="play_circle" size={54} color="rgba(255,255,255,.55)" />
      </Box>
    );

  return (
    <Box
      component="video"
      ref={trackerRef}
      src={src}
      controls
      className="w-full"
      sx={placeholderSx}
    />
  );
};

export const DocumentationImage = ({ media }) => {
  const src = useMediaObjectUrl(media);

  if (!src) return null;

  return (
    <Box component="figure" sx={{ margin: '0 0 18px' }}>
      <Box
        component="img"
        src={src}
        alt={media.fileName || ''}
        className="max-w-full"
        sx={{ borderRadius: '10px', border: '1px solid var(--border)' }}
      />
    </Box>
  );
};

export const DocumentationFileLink = ({ media }) => {
  const src = useMediaObjectUrl(media);
  const hosted = isHostedMedia(media);

  return (
    <Box
      component="a"
      href={src || undefined}
      download={hosted ? media.fileName : undefined}
      target={hosted ? undefined : '_blank'}
      rel="noreferrer"
      className="flex items-center self-start cursor-pointer"
      sx={{
        gap: '9px',
        border: '1px solid var(--border)',
        background: 'var(--panel)',
        borderRadius: '9px',
        padding: '9px 14px',
        fontSize: '12.5px',
        fontWeight: 600,
        color: 'var(--text) !important',
        textDecoration: 'none !important',
        ':hover': { borderColor: 'var(--accent-border)' },
      }}
    >
      <DocIcon name="attach_file" size={17} color="var(--accent)" />
      <span>{media.fileName || media.url}</span>
      {media.sizeBytes && (
        <Box component="span" sx={{ fontSize: '11px', color: 'var(--text2)' }}>
          {formatBytes(media.sizeBytes)}
        </Box>
      )}
    </Box>
  );
};
